import {
  createApiClient,
  statusOf,
  type Envelope,
  type Paginated,
  type Palette,
  type PlatformStats,
  type PlatformTenant,
  type TenantStatus,
} from '@bixo/ui'
import { queryOptions } from '@tanstack/react-query'
import type {
  Announcement,
  CustomDomain,
  Dashboard,
  Operator,
  Plan,
  PlatformRoleKey,
  PlatformRoles,
  PlatformAuditRow,
  PlatformInvoice,
  SubscriptionInfo,
  SupportGrant,
  TemplateSummary,
  TenantBackup,
  AppBuild,
  AppBuildDefaults,
} from './types'

let unauthorizedHandler: (() => void) | null = null

export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler
}

export const api = createApiClient({
  baseURL: '/papi/v1',
  onUnauthorized: () => unauthorizedHandler?.(),
})

const data = <T>(promise: Promise<{ data: Envelope<T> }>): Promise<T> => promise.then((r) => r.data.data)

export interface TenantsQuery {
  search?: string
  status?: TenantStatus
  plan?: string
  billing?: 'trial_ending' | 'trial_expired' | 'overdue' | 'in_grace' | 'paying' | 'complimentary'
  page?: number
}

export interface CreateTenantPayload {
  name: string
  slug: string
  owner_name: string
  owner_email: string
  owner_password: string
  palette?: Palette
}

export const endpoints = {
  login: (body: { email: string; password: string; remember?: boolean }) =>
    data<{ two_factor: boolean; user?: Operator }>(api.post('/auth/login', body)),
  twoFactorChallenge: (body: { code: string } | { recovery_code: string }) =>
    data<{ user: Operator }>(api.post('/auth/two-factor-challenge', body)),
  logout: () => api.post('/auth/logout').then(() => undefined),
  me: () => data<Operator>(api.get('/me')),
  changePassword: (body: { current_password: string; password: string; password_confirmation: string }) =>
    data<Operator>(api.put('/me/password', body)),
  forgotPassword: (email: string) => data<{ message: string }>(api.post('/auth/forgot-password', { email })),
  resetPassword: (body: { token: string; email: string; password: string; password_confirmation: string }) =>
    data<{ message: string }>(api.post('/auth/reset-password', body)),
  stats: () => data<PlatformStats>(api.get('/stats')),
  tenants: (params: TenantsQuery) =>
    api.get<Paginated<PlatformTenant>>('/tenants', { params }).then((r) => r.data),
  tenant: (id: string) => data<PlatformTenant>(api.get(`/tenants/${id}`)),
  createTenant: (body: CreateTenantPayload) => data<PlatformTenant>(api.post('/tenants', body)),
  suspendTenant: (id: string, reason?: string) =>
    data<PlatformTenant>(api.post(`/tenants/${id}/suspend`, reason ? { reason } : {})),
  activateTenant: (id: string) => data<PlatformTenant>(api.post(`/tenants/${id}/activate`)),
  palettes: () => data<{ key: Palette; label: string }[]>(api.get('/palettes')),

  // Phase 1: commercial and operations
  dashboard: () => data<Dashboard>(api.get('/dashboard')),
  plans: () => data<Plan[]>(api.get('/plans')),
  updatePlan: (key: string, body: Partial<Plan>) => data<Plan>(api.patch(`/plans/${key}`, body)),
  subscription: (tenantId: string) => data<SubscriptionInfo>(api.get(`/tenants/${tenantId}/subscription`)),
  assignPlan: (tenantId: string, body: { plan: string; cycle: 'monthly' | 'yearly' }) =>
    data<SubscriptionInfo>(api.post(`/tenants/${tenantId}/plan`, body)),
  extendTrial: (tenantId: string, days: number) =>
    data<SubscriptionInfo>(api.post(`/tenants/${tenantId}/extend-trial`, { days })),
  setOverrides: (tenantId: string, body: NonNullable<SubscriptionInfo['overrides']>) =>
    data<SubscriptionInfo>(api.put(`/tenants/${tenantId}/overrides`, body)),
  invoices: (params: { tenant_id?: string; page?: number }) =>
    api.get<Paginated<PlatformInvoice>>('/invoices', { params }).then((r) => r.data),
  domains: (tenantId: string) => data<CustomDomain[]>(api.get(`/tenants/${tenantId}/domains`)),
  supportAccess: (tenantId: string) =>
    data<{ active: SupportGrant | null }>(api.get(`/tenants/${tenantId}/support-access`)),
  impersonate: (tenantId: string) =>
    data<{ url: string; expires_at: string }>(api.post(`/tenants/${tenantId}/impersonate`)),
  backups: (tenantId: string) => data<TenantBackup[]>(api.get(`/tenants/${tenantId}/backups`)),
  runBackup: (tenantId: string) => data<{ queued: boolean }>(api.post(`/tenants/${tenantId}/backups`)),
  backupDownloadUrl: (id: number) => data<{ url: string }>(api.get(`/backups/${id}/download-url`)),
  restoreCheck: (id: number) => data<{ queued: boolean }>(api.post(`/backups/${id}/restore-check`)),
  appBuilds: (tenantId: string) =>
    data<{ defaults: AppBuildDefaults; builds: AppBuild[] }>(api.get(`/tenants/${tenantId}/app-builds`)),
  startAppBuild: (tenantId: string, body: FormData) =>
    data<AppBuild>(api.post(`/tenants/${tenantId}/app-builds`, body)),
  appBuildDownloadUrl: (id: number) =>
    data<{ url: string; expires_in_hours: number }>(api.get(`/app-builds/${id}/download-url`)),
  users: () => data<Operator[]>(api.get('/users')),
  roles: () => data<PlatformRoles>(api.get('/roles')),
  createUser: (body: { name: string; email: string; role: PlatformRoleKey; password?: string }) =>
    data<Operator & { temporary_password: string | null }>(api.post('/users', body)),
  updateUser: (id: number, body: { name: string; role?: PlatformRoleKey }) =>
    data<Operator>(api.patch(`/users/${id}`, body)),
  deactivateUser: (id: number) => data<Operator>(api.post(`/users/${id}/deactivate`)),
  reactivateUser: (id: number) => data<Operator>(api.post(`/users/${id}/reactivate`)),
  deleteUser: (id: number) => api.delete(`/users/${id}`).then(() => undefined),
  resetUserPassword: (id: number, body: { method: 'temporary' | 'email'; password?: string }) =>
    data<{ emailed: boolean; temporary_password: string | null }>(
      api.post(`/users/${id}/reset-password`, body),
    ),
  resetUserTwoFactor: (id: number) => data<Operator>(api.post(`/users/${id}/reset-two-factor`)),
  announcements: () => data<Announcement[]>(api.get('/announcements')),
  createAnnouncement: (body: Omit<Announcement, 'id' | 'created_at'>) =>
    data<Announcement>(api.post('/announcements', body)),
  updateAnnouncement: (id: number, body: Omit<Announcement, 'id' | 'created_at'>) =>
    data<Announcement>(api.patch(`/announcements/${id}`, body)),
  deleteAnnouncement: (id: number) => api.delete(`/announcements/${id}`).then(() => undefined),
  audit: (params: { action?: string; tenant_id?: string; search?: string; page?: number }) =>
    api.get<Paginated<PlatformAuditRow>>('/audit-logs', { params }).then((r) => r.data),
  myTwoFactorSetup: () =>
    data<{ secret: string; secret_formatted: string; otpauth_url: string }>(api.post('/me/two-factor')),
  myTwoFactorConfirm: (code: string) =>
    data<{ recovery_codes: string[] }>(api.post('/me/two-factor/confirm', { code })),
  myTwoFactorDisable: (password: string) =>
    api.delete('/me/two-factor', { data: { password } }).then(() => undefined),
  myRecoveryCodes: (password: string) =>
    data<{ recovery_codes: string[] }>(api.post('/me/two-factor/recovery-codes', { password })),
}

/** Public, pre-login endpoints on the platform host. */
export const publicEndpoints = {
  plans: () => data<Plan[]>(api.get('/public/plans')),
  templates: () => data<TemplateSummary[]>(api.get('/public/templates')),
  slugAvailability: (slug: string) =>
    data<{ slug: string; available: boolean; reason: string | null; domain: string }>(
      api.get('/public/slug-availability', { params: { slug } }),
    ),
  startSignup: (body: {
    business_name: string
    slug: string
    business_template: string
    owner_name: string
    email: string
    password: string
    plan?: string
    website?: string
  }) => data<{ signup_token: string; email: string; expires_at: string }>(api.post('/public/signup', body)),
  resendOtp: (signup_token: string) =>
    data<{ expires_at: string }>(api.post('/public/signup/resend', { signup_token })),
  verifyOtp: (signup_token: string, otp: string) =>
    data<{ tenant_id: string; status: string; url: string | null }>(
      api.post('/public/signup/verify', { signup_token, otp }),
    ),
  signupStatus: (signup_token: string) =>
    data<{ status: string; tenant_id: string | null; url: string | null }>(
      api.get('/public/signup/status', { params: { signup_token } }),
    ),
}

export const qk = {
  me: ['me'] as const,
  stats: ['stats'] as const,
  tenants: (q: TenantsQuery) => ['tenants', q] as const,
  tenant: (id: string) => ['tenant', id] as const,
  palettes: ['palettes'] as const,
  dashboard: ['dashboard'] as const,
  plans: ['plans'] as const,
  subscription: (id: string) => ['subscription', id] as const,
  invoices: (q: object) => ['invoices', q] as const,
  domains: (id: string) => ['domains', id] as const,
  support: (id: string) => ['support', id] as const,
  backups: (id: string) => ['backups', id] as const,
  appBuilds: (id: string) => ['app-builds', id] as const,
  users: ['users'] as const,
  roles: ['roles'] as const,
  announcements: ['announcements'] as const,
  audit: (q: object) => ['audit', q] as const,
}

export const meQuery = queryOptions({
  queryKey: qk.me,
  queryFn: async (): Promise<Operator | null> => {
    try {
      return await endpoints.me()
    } catch (error) {
      if (statusOf(error) === 401) return null
      throw error
    }
  },
  staleTime: 5 * 60_000,
  retry: false,
})

/** The public address of a workspace slug, derived from this portal's host. */
export function workspaceUrl(slug: string): string {
  const host = window.location.host.replace(/^platform\./, '')
  return `${window.location.protocol}//${slug || 'your-business'}.${host}`
}
